import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import PageService from '../../services/PageService'

const CreateCycleModal = ({
  testCases,
  dropdownElInfo,
  createCycleOpen,
  setCreateCycleOpen,
  setTestCasesToggler
}) => {
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [cycleName, setCycleName] = useState('')
  const [cycleNameError, setCycleNameError] = useState('')
  const [loading, setLoading] = useState(false)

  const availableVersions = useMemo(() => {
    return testCases.filter(v => v.type !== 'general').map(v => ({ id: v.id, name: v.name }))
  }, [testCases])

  const isVersionSelectDisabled =
    dropdownElInfo?.class === 'version' &&
    !dropdownElInfo?.isBlueprint &&
    !!dropdownElInfo?.id

  useEffect(() => {
    if (createCycleOpen) {
      if (isVersionSelectDisabled) {
        const preSelected = availableVersions.find(v => v.id === dropdownElInfo.id)
        if (preSelected) {
          setSelectedVersion(preSelected)
        } else if (availableVersions.length > 0) {
          setSelectedVersion(availableVersions[0])
        } else {
          setSelectedVersion(null)
        }
      } else {
        const preSelectedByVersionId = availableVersions.find(
          (v) => v.id === dropdownElInfo?.versionId
        )
        if (preSelectedByVersionId) {
          setSelectedVersion(preSelectedByVersionId)
        } else if (availableVersions.length > 0) {
          setSelectedVersion(availableVersions[0])
        } else {
          setSelectedVersion(null)
        }
      }

      setCycleName('')
      setCycleNameError('')
    }
  }, [createCycleOpen, dropdownElInfo, availableVersions, isVersionSelectDisabled])


  const handleCloseCreateDialog = () => {
    setCreateCycleOpen(false)
    setSelectedVersion(null)
    setCycleName('')
    setCycleNameError('')
  }

  const handleVersionChange = (e) => {
    const newVersionId = e.target.value
    const newVersion = availableVersions.find(v => v.id === newVersionId)
    setSelectedVersion(newVersion)
  }

  const handleCycleNameChange = (e) => {
    setCycleName(e.target.value)
    if (cycleNameError && e.target.value.trim()) {
      setCycleNameError('')
    }
  }


  const getTestCaseIdsFromBlueprintContext = () => {
    const isFromBlueprintFolder =
      dropdownElInfo?.class === 'folder' && dropdownElInfo?.isBlueprint
    const isFromBlueprintTestCase =
      dropdownElInfo?.class === 'testCase' && dropdownElInfo?.isBlueprint
    if (!isFromBlueprintFolder && !isFromBlueprintTestCase) return []

    const cases = dropdownElInfo?.testCases
    if (!Array.isArray(cases) || !cases.length) return []
    return cases.map((tc) => tc?.id).filter(Boolean)
  }

  const handleCreateCycle = async () => {
    let hasError = false

    if (!selectedVersion) {
      hasError = true
    }

    if (!cycleName.trim()) {
      setCycleNameError('Cycle Name is required.')
      hasError = true
    } else {
      setCycleNameError('')
    }

    if (!selectedVersion) {
      console.error('No version selected.')
      hasError = true
    }

    if (hasError) {
      return
    }

    try {
      setLoading(true)
      const createdCycle = await PageService.createTestCaseCycle({
        projectVersionId: selectedVersion.id,
        name: cycleName,
        description: '',
        versionTag: null,
        dateFrom: null,
        dateTo: null,
      })
      const cycleId = createdCycle?.id
      const testCaseIdsToAdd = getTestCaseIdsFromBlueprintContext()

      if (cycleId && testCaseIdsToAdd.length > 0) {
        await PageService.addTestsToCycle(cycleId, {
          testCaseIds: testCaseIdsToAdd,
        })
      }
      setTestCasesToggler((prev) => !prev)
      handleCloseCreateDialog()
    } catch (error) {
      if (error?.response?.status === 409) {
        setCycleNameError(error.response.data?.error || 'A cycle with this name already exists in this version. Please choose a different name.')
      } else {
        console.log(error)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={createCycleOpen} onClose={handleCloseCreateDialog}>
      <Box className="createTestCaseCycleModal">
        <DialogTitle className="createTestCaseCycleModalTitle">
          Create Cycle
        </DialogTitle>
        <DialogContent className="createTestCaseCycleModalContent">
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Version*</InputLabel>
            <Select
              value={selectedVersion ? selectedVersion.id : ''}
              label="Version*"
              onChange={handleVersionChange}
              disabled={isVersionSelectDisabled}
            >
              {availableVersions.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Cycle Name*"
            type="text"
            fullWidth
            variant="outlined"
            value={cycleName}
            onChange={handleCycleNameChange}
            error={!!cycleNameError}
            helperText={cycleNameError + ' '}
            inputProps={{ maxLength: 50 }}
            sx={{ mb: '4px' }}
          />

        </DialogContent>
        <DialogActions className="createTestCaseCycleModalActions">
          <Button className="createTestCaseCycleModalButton" onClick={handleCloseCreateDialog} color="secondary" variant="outlined">
            CANCEL
          </Button>
          {loading ? (
            <LoadingButton
              loading
              className="createTestCaseCycleModalButton"
              variant="contained"
              loadingIndicator={<CircularProgress color="white" size={16} />}
            />
          ) : (
            <Button className="createTestCaseCycleModalButton" onClick={handleCreateCycle} color="secondary" variant="contained" autoFocus>
              SAVE
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default CreateCycleModal
